/* eslint-disable no-param-reassign */

import produce, {Draft} from 'immer';
import {last} from 'lodash';

import {isNotNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Parameter} from '../../../editors/commons/parameterModel';
import {ExpressionId} from '../../../editors/expressions/expressionModel';
import {isExpressionRef} from '../../../editors/expressions/expressionRefModel';
import {isInferredTypeCompatibleWithManifest} from '../../../editors/expressions/expressionResultHelpers';
import {InferredType} from '../../../editors/expressions/infer/expressionResultModel';
import {ClientRuleAction, RuleAction} from '../../ruleActionModel';
import {ClientRuleCondition, RuleCondition} from '../../ruleConditionModel';
import {RuleIntegration} from '../../ruleIntegrationModel';
import {RuleEditorState} from './ruleEditorState';

interface InferAndValidateExpressionsOptions {
  expressionId?: ExpressionId;
  forceRemoveExpressionReferences?: boolean;
}

export function removeInvalidExpressionReferences(
  draft: Draft<RuleEditorState>,
  ruleIntegration: RuleIntegration,
  options: InferAndValidateExpressionsOptions = {},
) {
  const {expressions, expressionPanelState} = draft;

  const allExpressions = [...expressions, expressionPanelState.selectedExpression].filter(isNotNull);

  const {
    expressionId,
    forceRemoveExpressionReferences,
    // integrationsService
  } = options;

  if (expressionId && forceRemoveExpressionReferences) {
    maybeRemoveExpressionReferenceFromParams(draft, ruleIntegration, {expressionId});
  }

  allExpressions.forEach((expression) => {
    const newInferredType = last(expression.atoms)!.inferredType;

    maybeRemoveExpressionReferenceFromParams(draft, ruleIntegration, {
      expressionId: expression.id,
      inferredType: newInferredType,
    });
  });
}

interface RemoveExpressionReferenceOptions {
  expressionId: ExpressionId;
  inferredType?: InferredType;
}

function maybeRemoveExpressionReferenceFromParams(
  draft: Draft<RuleEditorState>,
  ruleIntegration: RuleIntegration,
  options: RemoveExpressionReferenceOptions,
) {
  draft.ruleState.actions = maybeRemoveExpressionReferenceFromConditionOrAction(
    draft.ruleState.actions,
    ruleIntegration,
    options,
  );

  draft.ruleState.conditions = draft.ruleState.conditions.map((c) =>
    maybeRemoveExpressionReferenceFromConditionOrAction(c, ruleIntegration, options),
  );

  return draft;
}

function maybeRemoveExpressionReferenceFromConditionOrAction(
  conditionsOrActions: Draft<ReadonlyArray<ClientRuleCondition>>,
  ruleIntegration: RuleIntegration,
  {expressionId, inferredType}: RemoveExpressionReferenceOptions,
): Draft<ReadonlyArray<ClientRuleCondition>>;
function maybeRemoveExpressionReferenceFromConditionOrAction(
  conditionsOrActions: Draft<ReadonlyArray<ClientRuleAction>>,
  ruleIntegration: RuleIntegration,
  {expressionId, inferredType}: RemoveExpressionReferenceOptions,
): Draft<ReadonlyArray<ClientRuleAction>>;
function maybeRemoveExpressionReferenceFromConditionOrAction(
  conditionsOrActions: Draft<ReadonlyArray<ClientRuleCondition | ClientRuleAction>>,
  ruleIntegration: RuleIntegration,
  {expressionId, inferredType}: RemoveExpressionReferenceOptions,
) {
  if (!ruleIntegration) {
    return conditionsOrActions;
  }

  return conditionsOrActions.map((conditionOrAction) => {
    const definition =
      ruleIntegration.findConditionDefinition(conditionOrAction as RuleCondition) ??
      ruleIntegration.findActionDefinition(conditionOrAction as RuleAction);

    if (!definition) {
      return conditionOrAction;
    }

    return produce(conditionOrAction, (draft: any) => {
      Object.entries(draft.params || {}).forEach(([param, paramValue]) => {
        const ruleParameter = definition?.parameters[param];

        if (!ruleParameter) {
          return;
        }

        if (
          !isReferencedInferredTypeCompatibleWithManifest(
            expressionId,
            paramValue,
            ruleParameter,
            inferredType,
          )
        ) {
          draft.params[param] = undefined;
        }

        if (Array.isArray(paramValue)) {
          const beforeLength = paramValue.length;
          draft.params[param] = paramValue.filter((p: any) =>
            isReferencedInferredTypeCompatibleWithManifest(expressionId, p, ruleParameter, inferredType),
          );

          // Prevent being `any` preselected if we removed last item
          if (beforeLength && draft.params[param].length === 0) {
            draft.params[param] = undefined;
          }
        }
      });

      return draft;
    });
  });
}

function isReferencedInferredTypeCompatibleWithManifest(
  expressionId: ExpressionId,
  paramValue: any,
  ruleParameter: Parameter,
  inferredType?: InferredType,
) {
  const isExpressionParameter = Parameter.isArray(ruleParameter)
    ? Parameter.isExpression(ruleParameter.items)
    : Parameter.isExpression(ruleParameter);

  const isExpressionReference =
    (isExpressionParameter && expressionId === paramValue) ||
    (isExpressionRef(paramValue) && paramValue.expression === expressionId);

  // If it's not referencing expression it's always compatible
  if (!isExpressionReference) {
    return true;
  }

  // When we don't have inferred type, like when we delete expression, it's not compatible
  if (!inferredType) {
    return false;
  }

  return isInferredTypeCompatibleWithManifest(inferredType, ruleParameter);
}
